import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { createEnum } from "@/lib/utils/createEnum";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { ExternalLink } from "@/components/ExternalLink";

export type S1InvestStrategies = {
  id: number;
  strategy_id: number;
  strategy_name: string;
  investment_strategy: string;
  reference_indicator: string;
  sfdr_classification?: string;
  frequency?: string;
  entry_exit_fee?: number;
  max_management_fee?: number;
  isr_score?: number;
  inception_date?: string;
  created_at: string;
  updated_at: string;
};

export const s1InvestStrategiesFields = createEnum<S1InvestStrategies>();

const t = (key: string) =>
  i18n.t(`${Tables.S1_INVEST_STRATEGIES}.columns.${key}`, { ns: "tables" });

export const s1_invest_strategies_columns: ColumnDef<S1InvestStrategies>[] = [
  {
    accessorKey: s1InvestStrategiesFields.id,
    header: () => t("id"),
  },
  {
    accessorKey: s1InvestStrategiesFields.strategy_name,
    header: () => t("strategy_name"),
    cell: ({ row }) => (
      <ExternalLink
        href={toWorkspacePath(
          `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_id}`,
        )}
        className="text-xs"
        underline="hover"
      >
        {row.original.strategy_name}
      </ExternalLink>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.investment_strategy,
    header: () => t("investment_strategy"),
    cell: ({ row }) => (
      <div className="line-clamp-3 max-w-sm whitespace-pre-wrap text-xs leading-5">
        {row.original.investment_strategy}
      </div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.reference_indicator,
    header: () => t("reference_indicator"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.reference_indicator}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.sfdr_classification,
    header: () => t("sfdr_classification"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.sfdr_classification}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.frequency,
    header: () => t("frequency"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.frequency}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.entry_exit_fee,
    header: () => t("entry_exit_fee"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.entry_exit_fee}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.max_management_fee,
    header: () => t("max_management_fee"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.max_management_fee}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.isr_score,
    header: () => t("isr_score"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.isr_score}</div>
    ),
  },
  {
    accessorKey: s1InvestStrategiesFields.inception_date,
    header: () => t("inception_date"),
    cell: ({ row }) => (
      <div className="text-xs">{row.original.inception_date}</div>
    ),
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Link
          href={`${window.location.pathname}/${row.getValue(s1InvestStrategiesFields.id)}`}
          className="text-xs"
          underline="hover"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
